import React, { useCallback, useMemo, useState } from "react";
import { LeadLink } from "web/react/components/lead-link";
import SaveForLaterButton from "web/react/components/save-for-later-button/save-for-later-button";
import { SizePicker } from "web/react/components/size-picker/size-picker";
import { Button } from "web/react/emo/button/button";
import { ProductCardHorizontal } from "web/react/emo/product-card-horizontal";
import {
    EventLabel,
    PinSizeInfo,
    useSaveForLater,
} from "web/react/hooks/use-save-for-later/use-save-for-later";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import {
    ProductBuyOptionsSerializer,
    ProductPageAreaSerializer,
    ProductScreenSize,
} from "web/types/serializers";
import * as styles from "./product-area.css";

export interface ProductAreaProps {
    productArea: ProductPageAreaSerializer;
    uniqueRetailerCount: number;
    isCheckout: boolean;
    productDetailsContainerRef: React.RefObject<HTMLDivElement>;
    mainBuyOption: ProductBuyOptionsSerializer;
}

export function ProductArea({
    productArea,
    uniqueRetailerCount,
    isCheckout,
    productDetailsContainerRef,
    mainBuyOption,
}: ProductAreaProps): React.ReactElement {
    const productId = productArea.product.product_id;
    const isUserLoggedIn = userProfiler.isLoggedIn();
    const productIsSavedButtonText =
        uniqueRetailerCount > 1
            ? gettext("oos_product.tracking_mutiple_retailers_notification", {
                  retailer_count: uniqueRetailerCount,
              })
            : gettext("oos_product.tracking_single_retailer_notification");

    const BASE_AFFILIATE_ANALYTICS_CONFIG = useMemo(
        () =>
            ({
                href: mainBuyOption.affiliate_url,
                eventCategory: "oos-product-hero",
                eventAction: "click",
                eventCustomData: {
                    buy_option_id: mainBuyOption.link_id.toString(),
                    product_id: productId,
                    retailer_slug: mainBuyOption.retailer_slug,
                },
            } as const),
        [mainBuyOption, productId]
    );

    const sizes = productArea.sizes.all_sizes;
    const [selectedSize, setSelectedSize] = useState<ProductScreenSize | undefined>(undefined);
    const [isSizePickerExpanded, setSizePickerExpanded] = useState<boolean>(false);
    const schemas = productArea.sizes.schema_labels;
    const userDefaultSchema = productArea.sizes.user_default_schema;
    const hasSizes = sizes.length > 0 && sizes.every((size) => size.localized_sizes);
    const { isSaved: productIsSaved, toggleSaveForLater } = useSaveForLater(
        productArea.product.product_id,
        EventLabel.OOS,
        undefined,
        undefined
    );

    const convertSizeToPinInfo = useCallback((size: ProductScreenSize): PinSizeInfo[] | null => {
        if (!size.localized_sizes || !size.user_default_schema) {
            return null;
        }
        const localizedSize = size.localized_sizes[size.user_default_schema];

        if (!localizedSize || !localizedSize.tag) {
            return null;
        }

        // The tag might be a JSON string like:
        // '{"first_dimension": "size.jeans.au.w.16", "second_dimension": "size.jeans.inseam.inch.30"}'
        const sizeTag = localizedSize.tag;
        let pinSizes: PinSizeInfo[] = [];
        if (sizeTag.startsWith("{")) {
            try {
                const parsedTag = JSON.parse(sizeTag);
                pinSizes = Object.values(parsedTag).map((dimensionTag: any) => ({
                    size_tag: dimensionTag,
                }));
            } catch (e) {
                console.warn(
                    `Failed to parse multi-dimensional size tag: ${sizeTag}, threw error ${e}`
                );
            }
        } else {
            pinSizes.push({ size_tag: sizeTag }); // tag: "size.dress.uk.10"
        }
        return pinSizes;
    }, []);

    const handleSizePickerSelect = useCallback(
        (option: any) => {
            const size = option.value as ProductScreenSize;
            const sizeInfo = convertSizeToPinInfo(size);
            setSelectedSize(size);
            setSizePickerExpanded(false);

            if (sizeInfo) {
                toggleSaveForLater(sizeInfo);
            }
        },
        [convertSizeToPinInfo, toggleSaveForLater]
    );

    const trackCheckoutClick = useCallback(
        (label: string) => {
            if (isCheckout) {
                analytics.event(
                    "oos-product-hero", // category
                    "click",
                    label,
                    false, // nonInteraction
                    {
                        buy_option_id: mainBuyOption.link_id.toString(),
                        product_id: productId,
                        retailer_slug: mainBuyOption.retailer_slug,
                    } // customData
                );
                productDetailsContainerRef.current?.scrollIntoView({ behavior: "smooth" });
            }
        },
        [isCheckout, mainBuyOption, productId, productDetailsContainerRef]
    );

    const ImageContainer = useCallback(
        ({ children }: { children: React.ReactNode }): React.ReactElement => {
            return isCheckout ? (
                <div
                    onClick={() => trackCheckoutClick("checkout-gallery-image")}
                    data-testid="product-image"
                    className={styles.cursorPointer}
                >
                    {children}
                </div>
            ) : (
                <LeadLink
                    {...BASE_AFFILIATE_ANALYTICS_CONFIG}
                    openInNewTab
                    reason="affiliate-gallery-image"
                    eventLabel="affiliate-gallery-image"
                    dataTestId="product-image"
                >
                    {children}
                </LeadLink>
            );
        },
        [isCheckout, trackCheckoutClick, BASE_AFFILIATE_ANALYTICS_CONFIG]
    );

    const ViewProductDetailsContainer = useCallback(
        ({ children }: { children: React.ReactNode }): React.ReactElement => {
            return isCheckout ? (
                <div
                    onClick={() => trackCheckoutClick("checkout-view-product-details")}
                    data-testid="view-product-details"
                    className={styles.cursorPointer}
                >
                    {children}
                </div>
            ) : (
                <LeadLink
                    {...BASE_AFFILIATE_ANALYTICS_CONFIG}
                    openInNewTab
                    reason="affiliate-view-product-details"
                    eventLabel="affiliate-view-product-details"
                    dataTestId="view-product-details"
                >
                    {children}
                </LeadLink>
            );
        },
        [isCheckout, trackCheckoutClick, BASE_AFFILIATE_ANALYTICS_CONFIG]
    );

    return (
        <div className={styles.productAreaContainer}>
            <ProductCardHorizontal
                product={productArea.product}
                designer={productArea.designer}
                buyOption={productArea.buy_options[0]}
                ImageContainer={ImageContainer}
                ViewProductDetailsContainer={ViewProductDetailsContainer}
            >
                {uniqueRetailerCount > 0 && (
                    <SaveForLaterButton
                        type="cta"
                        cta={{
                            title: gettext("oos_product.get_notified_when_back_in_stock_btn"),
                        }}
                        initialIsSaved={productIsSaved}
                        activeCta={{
                            title: productIsSavedButtonText,
                            icon: "notification-active",
                            iconPosition: "right",
                        }}
                        {...(isUserLoggedIn && { onClick: toggleSaveForLater })}
                        pageSource="oos"
                        productId={productId}
                        isLoggedIn={isUserLoggedIn}
                        size="lg"
                        variant="primary"
                        width="full"
                    />
                )}
                {hasSizes && productIsSaved && (
                    <div style={{ marginTop: "5px" }}>
                        <SizePicker
                            onSelect={handleSizePickerSelect}
                            isExpanded={isSizePickerExpanded}
                            schemas={schemas}
                            defaultSelectText={gettext(
                                "oos_product.select_size_for_personalized_alerts"
                            )}
                            userDefaultSchema={userDefaultSchema}
                            setExpanded={(expanded: boolean): void =>
                                setSizePickerExpanded(expanded)
                            }
                            sizes={sizes}
                            productType={productArea.product_purchase_type}
                            selectedOption={selectedSize}
                            isExpressCheckout={false}
                            isComparison={false}
                            showDisabledOptions={true}
                            showAllSchemas={true}
                            selectedTextPrefix={
                                selectedSize
                                    ? ` ${gettext("oos_product.alert_set_for_size")} `
                                    : undefined
                            }
                        />
                    </div>
                )}
                <Button title={"CTA 3"} size="lg" variant="secondary" width="full" />
            </ProductCardHorizontal>
        </div>
    );
}
